/**
 * Mushroom Hunt game engine — canvas-based, no game-engine deps.
 *
 * World coords are based on a virtual 720x1000 playfield. The canvas is scaled
 * to whatever pixel size the wrapper has, preserving aspect ratio. This keeps
 * the gameplay feel consistent across screen sizes.
 */

import type { Mushroom } from "../types";
import { audio } from "./audio";

export const PLAY_W = 720;
export const PLAY_H = 1000;
const BASKET_Y = PLAY_H - 110;
const BASKET_W = 160;
const BASKET_H = 80;
const CATCH_MARGIN_X = 22; // catch tolerance (vs basket inner walls)
const MUSHROOM_R = 68; // visual radius — also collision radius
const SPAWN_MIN_X = MUSHROOM_R + 10;
const SPAWN_MAX_X = PLAY_W - MUSHROOM_R - 10;
const MAX_LIVES = 3;
const MAX_CONCURRENT_FALLING = 3;

export type GameState = "menu" | "playing" | "gameover";

export interface FallingMushroom {
  m: Mushroom;
  x: number;
  y: number;
  vy: number;
  rot: number;
  rotV: number;
  caught: boolean;
  missed: boolean;
}

export interface Particle {
  x: number; y: number; vx: number; vy: number;
  life: number; maxLife: number;
  color: string; size: number;
}

export interface FloatText {
  x: number; y: number; vy: number;
  life: number; maxLife: number;
  text: string; color: string; size: number;
}

export interface GameSnapshot {
  state: GameState;
  score: number;
  streak: number;
  bestStreak: number;
  caught: number;
  missed: number;
  lives: number;
  level: number;
  multiplier: number;
  basket: { x: number; y: number; w: number; h: number };
  falling: FallingMushroom[];
  particles: Particle[];
  floats: FloatText[];
  flash: { color: string; alpha: number } | null;
  shake: number;
  lastCaught: Mushroom | null;
  lastCaughtCategory: Mushroom["category"] | null;
  durationMs: number;
  causeOfDeath: Mushroom | null;
}

interface Input {
  leftHeld: boolean;
  rightHeld: boolean;
  pointerX: number | null;  // null = keyboard, number = mouse/touch target X in world coords
}

export class GameEngine {
  private state: GameState = "menu";
  private score = 0;
  private streak = 0;
  private bestStreak = 0;
  private caught = 0;
  private missed = 0;
  private lives = MAX_LIVES;
  private level = 1;
  private spawnedThisLevel = 0;

  private basketX = PLAY_W / 2;
  private basketVx = 0;

  private falling: FallingMushroom[] = [];
  private particles: Particle[] = [];
  private floats: FloatText[] = [];

  private nextSpawnIn = 800; // ms
  private startedAt = 0;

  private flash: { color: string; alpha: number } | null = null;
  private shake = 0;
  private lastCaught: Mushroom | null = null;
  private lastCaughtCategory: Mushroom["category"] | null = null;
  private causeOfDeath: Mushroom | null = null;

  private input: Input = { leftHeld: false, rightHeld: false, pointerX: null };

  private israeliPool: Mushroom[] = [];
  private middleEastBias = false;
  // When the UI language requires a translated name (English or Russian),
  // restrict spawns to species that actually have a name in that language so
  // the player never sees a placeholder Latin/Hebrew label.
  private requireLanguage: "english" | "russian" | null = null;

  constructor(private catalog: Mushroom[]) {
    if (!catalog.length) throw new Error("empty catalog");
    this.rebuildPools();
  }

  // ---------- public control ----------

  setCatalog(catalog: Mushroom[]) {
    this.catalog = catalog;
    this.rebuildPools();
  }

  setMiddleEastBias(on: boolean) {
    this.middleEastBias = on;
  }

  /**
   * When set, the spawn pool is restricted to species that have a name in the
   * given language ("english" -> english_common_name; "russian" ->
   * russian_common_name). Pass null to spawn from everything (Hebrew + Latin
   * modes always have a name, so no restriction is needed).
   */
  setRequiredLanguage(lang: "english" | "russian" | null) {
    this.requireLanguage = lang;
  }

  private rebuildPools() {
    this.israeliPool = this.catalog.filter((m) => m.israeli_source);
  }

  start() {
    this.state = "playing";
    this.score = 0;
    this.streak = 0;
    this.bestStreak = 0;
    this.caught = 0;
    this.missed = 0;
    this.lives = MAX_LIVES;
    this.level = 1;
    this.spawnedThisLevel = 0;
    this.basketX = PLAY_W / 2;
    this.basketVx = 0;
    this.falling = [];
    this.particles = [];
    this.floats = [];
    this.nextSpawnIn = 900;
    this.startedAt = performance.now();
    this.flash = null;
    this.shake = 0;
    this.lastCaught = null;
    this.lastCaughtCategory = null;
    this.causeOfDeath = null;
  }

  /**
   * Reset transient state so the canvas renders a clean menu scene again
   * (centered basket, no falling mushrooms / particles / flash / shake
   * leftover from a finished game). Without this, returning to the home
   * page after game over shows the frozen final-frame artifacts behind the
   * menu overlay until the next game starts.
   */
  goToMenu() {
    this.state = "menu";
    this.basketX = PLAY_W / 2;
    this.basketVx = 0;
    this.falling = [];
    this.particles = [];
    this.floats = [];
    this.flash = null;
    this.shake = 0;
    this.lastCaught = null;
    this.lastCaughtCategory = null;
    this.causeOfDeath = null;
  }

  setLeft(v: boolean) { this.input.leftHeld = v; this.input.pointerX = null; }
  setRight(v: boolean) { this.input.rightHeld = v; this.input.pointerX = null; }
  setPointer(worldX: number | null) {
    this.input.pointerX = worldX;
  }

  snapshot(): GameSnapshot {
    return {
      state: this.state,
      score: this.score,
      streak: this.streak,
      bestStreak: this.bestStreak,
      caught: this.caught,
      missed: this.missed,
      lives: this.lives,
      level: this.level,
      multiplier: this.currentMultiplier(),
      basket: { x: this.basketX, y: BASKET_Y, w: BASKET_W, h: BASKET_H },
      falling: this.falling,
      particles: this.particles,
      floats: this.floats,
      flash: this.flash,
      shake: this.shake,
      lastCaught: this.lastCaught,
      lastCaughtCategory: this.lastCaughtCategory,
      durationMs: this.startedAt ? performance.now() - this.startedAt : 0,
      causeOfDeath: this.causeOfDeath,
    };
  }

  // ---------- main step ----------

  step(dtMs: number) {
    if (this.state !== "playing") return;
    const dt = Math.min(dtMs, 50) / 1000; // clamp to 50ms (avoid alt-tab giant dt)

    this.updateBasket(dt);
    this.updateFalling(dt);
    this.maybeSpawn(dtMs);
    this.updateParticles(dt);
    this.updateFloats(dt);
    this.decayEffects(dt);

    // level-up: every 15 caught mushrooms (any kind hit)
    const spawnsPerLevel = 14 + this.level * 4;
    if (this.spawnedThisLevel >= spawnsPerLevel) {
      this.level += 1;
      this.spawnedThisLevel = 0;
      this.addFloat(PLAY_W / 2, PLAY_H / 2, `LEVEL ${this.level}`, "#ffd84d", 48);
      audio.play("level_up");
    }
  }

  // ---------- updates ----------

  private updateBasket(dt: number) {
    const accel = 4800;
    const max = 1100;
    const friction = 12;

    if (this.input.pointerX != null) {
      // Pointer mode: smooth seek
      const dx = this.input.pointerX - this.basketX;
      this.basketVx = clamp(dx * 14, -max, max);
    } else {
      if (this.input.leftHeld) this.basketVx -= accel * dt;
      if (this.input.rightHeld) this.basketVx += accel * dt;
      if (!this.input.leftHeld && !this.input.rightHeld) {
        // smooth stop
        this.basketVx -= this.basketVx * Math.min(friction * dt, 1);
      }
      this.basketVx = clamp(this.basketVx, -max, max);
    }

    this.basketX += this.basketVx * dt;
    const minX = BASKET_W / 2 + 8;
    const maxX = PLAY_W - BASKET_W / 2 - 8;
    if (this.basketX < minX) { this.basketX = minX; this.basketVx = 0; }
    if (this.basketX > maxX) { this.basketX = maxX; this.basketVx = 0; }
  }

  private updateFalling(dt: number) {
    const survivors: FallingMushroom[] = [];
    for (const f of this.falling) {
      if (f.caught || f.missed) continue;
      f.y += f.vy * dt;
      f.rot += f.rotV * dt;

      // Collision with basket rim (top of basket)
      const inXband = Math.abs(f.x - this.basketX) < (BASKET_W / 2 - CATCH_MARGIN_X);
      const inYband = f.y + MUSHROOM_R * 0.4 >= BASKET_Y - 6 && f.y - MUSHROOM_R * 0.5 <= BASKET_Y + 20;
      if (inXband && inYband) {
        this.handleCatch(f);
        continue;
      }

      // Off-screen bottom => missed
      if (f.y - MUSHROOM_R > PLAY_H) {
        this.handleMiss(f);
        continue;
      }
      survivors.push(f);
    }
    this.falling = survivors;
  }

  private handleCatch(f: FallingMushroom) {
    f.caught = true;
    this.caught += 1;
    this.lastCaught = f.m;
    this.lastCaughtCategory = f.m.category;

    const cat = f.m.category;
    if (cat === "deadly") {
      this.score = Math.max(0, this.score);
      this.streak = 0;
      this.flash = { color: "#ff2233", alpha: 1 };
      this.shake = 60;
      this.causeOfDeath = f.m;
      this.lives = 0;
      this.emitSplat(f.x, BASKET_Y - 10, "#7a0000", 60);
      audio.play("deadly");
      this.gameOver();
      return;
    }

    if (cat === "poison") {
      const loss = Math.abs(f.m.points);
      this.score = Math.max(0, this.score - loss);
      this.streak = 0;
      this.flash = { color: "#7fff00", alpha: 0.7 };
      this.shake = 22;
      this.lives -= 1;
      this.addFloat(f.x, BASKET_Y - 30, `-${loss} POISON`, "#7fff00", 28);
      this.emitSplat(f.x, BASKET_Y - 10, "#7fff00", 30);
      audio.play("poison");
      if (this.lives <= 0) {
        this.causeOfDeath = f.m;
        this.gameOver();
      }
      return;
    }

    if (cat === "medicinal") {
      // Restores one heart (capped). No score, streak preserved.
      const gained = this.lives < MAX_LIVES ? 1 : 0;
      this.lives = Math.min(MAX_LIVES, this.lives + 1);
      this.flash = { color: "#ff6ec7", alpha: 0.55 };
      const msg = gained ? "+1 LIFE" : "MEDICINAL";
      this.addFloat(f.x, BASKET_Y - 30, msg, "#ff6ec7", 28);
      this.emitSparkle(f.x, BASKET_Y - 10, "#ff6ec7", 36);
      this.spawnedThisLevel += 1;
      audio.play("medicinal");
      return;
    }

    if (cat === "inedible") {
      // Not toxic, not nutritious. No score, no penalty, streak preserved.
      // Tell the player why so the catch feels educational, not buggy.
      this.addFloat(f.x, BASKET_Y - 30, "INEDIBLE", "#a8a8a8", 24);
      this.emitSparkle(f.x, BASKET_Y - 10, "#a8a8a8", 8);
      this.spawnedThisLevel += 1;
      audio.play("inedible");
      return;
    }

    // Edible
    this.streak += 1;
    if (this.streak > this.bestStreak) this.bestStreak = this.streak;
    const mult = this.currentMultiplier();
    const gained = Math.round(f.m.points * mult);
    this.score += gained;
    this.spawnedThisLevel += 1;

    const color =
      cat === "edible_rare" ? "#ffd84d"
      : cat === "edible_choice" ? "#7be3ff"
      : "#9bf07a";
    this.addFloat(f.x, BASKET_Y - 30, `+${gained}${mult > 1 ? ` x${mult}` : ""}`, color, 26);
    this.emitSparkle(f.x, BASKET_Y - 10, color, cat === "edible_rare" ? 40 : 20);

    if (cat === "edible_rare") audio.play("catch_rare");
    else if (cat === "edible_choice") audio.play("catch_choice");
    else audio.play("catch_common");
  }

  private handleMiss(f: FallingMushroom) {
    f.missed = true;
    this.missed += 1;
    // Only edible misses cost a life; poison/deadly misses are good.
    if (f.m.category.startsWith("edible")) {
      this.streak = 0;
      this.lives -= 1;
      this.shake = 12;
      this.addFloat(f.x, PLAY_H - 160, "MISS", "#ff8d8d", 26);
      audio.play("miss");
      if (this.lives <= 0) this.gameOver();
    }
  }

  private gameOver() {
    this.state = "gameover";
    audio.play("game_over");
  }

  private currentMultiplier(): number {
    if (this.streak >= 20) return 4;
    if (this.streak >= 12) return 3;
    if (this.streak >= 6) return 2;
    return 1;
  }

  // ---------- spawning ----------

  private maybeSpawn(dtMs: number) {
    this.nextSpawnIn -= dtMs;
    if (this.nextSpawnIn > 0) return;
    if (this.falling.length >= MAX_CONCURRENT_FALLING) {
      this.nextSpawnIn = 120;
      return;
    }

    // Pick category based on level. Higher levels => more poison/deadly.
    const roll = Math.random();
    const dangerBoost = Math.min(0.25, (this.level - 1) * 0.03);
    let target: Mushroom["category"];
    if (roll < 0.04 + dangerBoost * 0.4) target = "deadly";
    else if (roll < 0.20 + dangerBoost) target = "poison";
    else if (roll < 0.28) target = "inedible";
    else if (roll < 0.33) target = "medicinal";
    else if (roll < 0.40) target = "edible_rare";
    else if (roll < 0.60) target = "edible_choice";
    else target = "edible_common";

    // Middle East players: spawn exclusively from the Israeli scrape pool.
    // The Israeli set is thin in some categories (e.g. zero edible_rare), so
    // instead of falling back to the wider catalog -- which would let
    // non-Israeli species through -- downgrade the target until we land in a
    // non-empty Israeli bucket.
    const meBias = this.middleEastBias && this.israeliPool.length > 0;
    const basePool = meBias ? this.israeliPool : this.catalog;
    const hasLangName = (m: Mushroom): boolean => {
      if (this.requireLanguage === "english") return !!m.english_common_name;
      if (this.requireLanguage === "russian") return !!m.russian_common_name;
      return true;
    };
    const sourcePool = basePool.filter(hasLangName);
    let pool = sourcePool.filter((m) => m.category === target);
    if (!pool.length && meBias) {
      const fallbackOrder: Mushroom["category"][] = [
        target, "edible_choice", "edible_common", "inedible", "poison", "medicinal",
      ];
      for (const t of fallbackOrder) {
        pool = this.israeliPool.filter((m) => m.category === t && hasLangName(m));
        if (pool.length) break;
      }
    }
    // Final fallbacks: respect language first, then anything in the catalog.
    if (!pool.length) pool = this.catalog.filter((m) => m.category === target && hasLangName(m));
    if (!pool.length) pool = this.catalog.filter((m) => m.category === target);
    const pick = pool.length ? pool[Math.floor(Math.random() * pool.length)] : this.catalog[0];

    // Start very slow; speed up smoothly as the player earns points.
    // At 0 score: ~140 px/s. Reaches ~440 px/s around 2000 score, caps at ~520.
    const scoreFactor = 1 + Math.min(this.score / 700, 2.7);
    const baseSpeed = 140 * scoreFactor + this.level * 6;
    const speedJitter = 0.85 + Math.random() * 0.3;

    this.falling.push({
      m: pick,
      x: SPAWN_MIN_X + Math.random() * (SPAWN_MAX_X - SPAWN_MIN_X),
      y: -MUSHROOM_R * 1.2,
      vy: baseSpeed * speedJitter,
      rot: (Math.random() - 0.5) * 0.4,
      rotV: (Math.random() - 0.5) * 1.2,
      caught: false,
      missed: false,
    });

    // Spawn cadence: leisurely at first, tightens with score and level
    const spawnFactor = 1 - Math.min(this.score / 1800, 0.55);
    const base = 1100 * spawnFactor - this.level * 30;
    this.nextSpawnIn = Math.max(380, base + (Math.random() - 0.5) * 260);
  }

  // ---------- effects ----------

  private emitSparkle(x: number, y: number, color: string, n: number) {
    for (let i = 0; i < n; i++) {
      const angle = Math.random() * Math.PI * 2;
      const speed = 60 + Math.random() * 220;
      this.particles.push({
        x, y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed - 80,
        life: 0, maxLife: 0.6 + Math.random() * 0.4,
        color,
        size: 3 + Math.random() * 4,
      });
    }
  }

  private emitSplat(x: number, y: number, color: string, n: number) {
    for (let i = 0; i < n; i++) {
      const angle = -Math.PI / 2 + (Math.random() - 0.5) * Math.PI;
      const speed = 200 + Math.random() * 400;
      this.particles.push({
        x, y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        life: 0, maxLife: 0.7 + Math.random() * 0.6,
        color,
        size: 4 + Math.random() * 6,
      });
    }
  }

  private addFloat(x: number, y: number, text: string, color: string, size: number) {
    this.floats.push({
      x, y, vy: -90, life: 0, maxLife: 1.0,
      text, color, size,
    });
  }

  private updateParticles(dt: number) {
    const out: Particle[] = [];
    for (const p of this.particles) {
      p.life += dt;
      if (p.life >= p.maxLife) continue;
      p.vy += 800 * dt; // gravity
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      out.push(p);
    }
    this.particles = out;
  }

  private updateFloats(dt: number) {
    const out: FloatText[] = [];
    for (const f of this.floats) {
      f.life += dt;
      if (f.life >= f.maxLife) continue;
      f.y += f.vy * dt;
      out.push(f);
    }
    this.floats = out;
  }

  private decayEffects(dt: number) {
    if (this.flash) {
      this.flash.alpha -= dt * 1.6;
      if (this.flash.alpha <= 0) this.flash = null;
    }
    if (this.shake > 0) {
      this.shake = Math.max(0, this.shake - dt * 80);
    }
  }
}

function clamp(v: number, lo: number, hi: number) {
  return v < lo ? lo : v > hi ? hi : v;
}
